/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║                                                                           ║
 * ║                     🐛 WOOORMS - GAME CONFIGURATION 🐛                    ║
 * ║                                                                           ║
 * ║  This file contains all shared game settings used by both client          ║
 * ║  and server. Tweak these values to adjust gameplay balance.               ║
 * ║                                                                           ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                           📦 VERSION                                      ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Game version - single source of truth for both client and server
 */
export const GAME_VERSION = '0.854';

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                           🌍 WORLD SETTINGS                               ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * World geometry and boundaries
 */
export const WORLD = {
	/** Total world radius in game units (large world for epic gameplay) */
	RADIUS: 25000,
	/** Thickness of the border danger zone */
	BORDER_THICKNESS: 100,
} as const;

/**
 * @deprecated Use WORLD.RADIUS instead
 */
export const WORLD_RADIUS = WORLD.RADIUS;
/**
 * @deprecated Use WORLD.BORDER_THICKNESS instead
 */
export const WORLD_BORDER_THICKNESS = WORLD.BORDER_THICKNESS;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                          ⚡ SERVER SETTINGS                               ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Server tick rate and timing configuration
 */
export const SERVER = {
	/** Server updates per second (higher = smoother but more CPU) */
	TICK_RATE: 30,
	/** Calculated tick interval in milliseconds */
	get TICK_INTERVAL() { return 1000 / this.TICK_RATE; },
	/** Server port for WebSocket connections */
	PORT: 2567,
	/** Room name for matchmaking */
	ROOM_NAME: 'game_room',
	/** Maximum players allowed per room */
	MAX_PLAYERS_PER_ROOM: 50,
} as const;

/** @deprecated Use SERVER.TICK_RATE instead */
export const TICK_RATE = SERVER.TICK_RATE;
/** @deprecated Use SERVER.TICK_INTERVAL instead */
export const TICK_INTERVAL = 1000 / SERVER.TICK_RATE;
/** @deprecated Use SERVER.PORT instead */
export const SERVER_PORT = SERVER.PORT;
/** @deprecated Use SERVER.ROOM_NAME instead */
export const ROOM_NAME = SERVER.ROOM_NAME;
/** @deprecated Use SERVER.MAX_PLAYERS_PER_ROOM instead */
export const MAX_PLAYERS_PER_ROOM = SERVER.MAX_PLAYERS_PER_ROOM;

/**
 * Client rendering configuration
 */
export const CLIENT = {
	/** Target frames per second for rendering */
	TARGET_FPS: 60,
	/** Client development port */
	PORT: 5173,
	/** Buffer time for entity interpolation (ms) - ~2 server ticks */
	INTERPOLATION_BUFFER_MS: 66,
} as const;

/**
 * Canvas size limits for performance on ultra-wide monitors
 * Prevents excessive rendering on 4K+ and ultra-wide displays
 */
export const CANVAS_LIMITS = {
	/** Maximum canvas width in pixels (2560 = common 1440p/4K width) */
	MAX_WIDTH: 2560,
	/** Maximum canvas height in pixels (1440 = common 1440p height) */
	MAX_HEIGHT: 1440,
	/** Whether to enable canvas size limiting */
	ENABLED: true,
} as const;

/** @deprecated Use CLIENT.TARGET_FPS instead */
export const TARGET_FPS = CLIENT.TARGET_FPS;
/** @deprecated Use CLIENT.PORT instead */
export const CLIENT_PORT = CLIENT.PORT;
/** @deprecated Use CLIENT.INTERPOLATION_BUFFER_MS instead */
export const INTERPOLATION_BUFFER_MS = CLIENT.INTERPOLATION_BUFFER_MS;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                          🐛 WORM MECHANICS                                ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Base worm properties
 */
export const WORM = {
	/** Base movement speed in units per second */
	DEFAULT_SPEED: 210,
	/** Speed multiplier when boosting (1.5x - slightly faster sprint) */
	BOOST_MULTIPLIER: 1.5,
	/** Starting radius for new worms */
	DEFAULT_RADIUS: 15,
	/** Starting segment count for new players (head, body, tail = minimum) */
	DEFAULT_SEGMENTS: 3,
	/** Starting score for new players */
	STARTING_SCORE: 5,
	/** Starting segment count for AI worms (they start bigger!) */
	AI_INITIAL_SEGMENTS: 40,
	/** Distance between segment centers */
	SEGMENT_SPACING: 20,
	/** Maximum number of segments a worm can have (scales to 10M+ score) */
	MAX_SEGMENTS: 50000,
} as const;

/**
 * Fuel system for boost
 * Fuel is consumed when boosting, based on current speed
 */
export const FUEL = {
	/** Maximum fuel capacity */
	MAX_FUEL: 100,
	/** Starting fuel (full tank) */
	STARTING_FUEL: 100,
	/** Base fuel burn rate per second when boosting */
	BASE_BURN_RATE: 15,
	/** Additional burn rate per speed unit (faster = more fuel used) */
	SPEED_BURN_MULTIPLIER: 0.05,
	/** Fuel regeneration rate per second when not boosting */
	REGEN_RATE: 8,
	/** Minimum fuel required to start boosting */
	MIN_TO_BOOST: 5,
} as const;

/**
 * Slow mode score generation
 * Players gain score passively when not boosting (moving slowly)
 * Creates a risk/reward trade-off between speed and score
 */
export const SLOW_MODE = {
	/** Score points generated per second when in slow mode */
	SCORE_PER_SECOND: 1,
	/** Minimum time in seconds not boosting before slow mode activates */
	ACTIVATION_DELAY: 0.5,
} as const;

/** @deprecated Use WORM.DEFAULT_SPEED instead */
export const DEFAULT_WORM_SPEED = WORM.DEFAULT_SPEED;
/** @deprecated Use WORM.BOOST_MULTIPLIER instead */
export const BOOST_SPEED_MULTIPLIER = WORM.BOOST_MULTIPLIER;
/** @deprecated Use WORM.DEFAULT_RADIUS instead */
export const DEFAULT_WORM_RADIUS = WORM.DEFAULT_RADIUS;
/** @deprecated Use WORM.DEFAULT_SEGMENTS instead */
export const DEFAULT_SEGMENT_COUNT = WORM.DEFAULT_SEGMENTS;
/** Starting score for new players */
export const STARTING_SCORE = WORM.STARTING_SCORE;
/** @deprecated Use WORM.AI_INITIAL_SEGMENTS instead */
export const AI_INITIAL_SEGMENT_COUNT = WORM.AI_INITIAL_SEGMENTS;
/** @deprecated Use WORM.SEGMENT_SPACING instead */
export const SEGMENT_SPACING = WORM.SEGMENT_SPACING;
/** @deprecated Use WORM.MAX_SEGMENTS instead */
export const MAX_SEGMENTS = WORM.MAX_SEGMENTS;

/**
 * Turn rate configuration (radians per second)
 * Controls how quickly worms can change direction
 */
export const TURN_RATE = {
	/** Normal turning speed (~143°/sec) - balanced control, requires skill */
	NORMAL: 2.5,
	/** Reduced turn rate while boosting (~115°/sec) - adds skill ceiling */
	BOOSTING: 2.0,
	/** Enhanced turn rate with agility powerup (~286°/sec) */
	AGILITY: 5.0,
} as const;

/** @deprecated Use TURN_RATE.NORMAL instead */
export const TURN_RATE_NORMAL = TURN_RATE.NORMAL;
/** @deprecated Use TURN_RATE.BOOSTING instead */
export const TURN_RATE_BOOSTING = TURN_RATE.BOOSTING;
/** @deprecated Use TURN_RATE.AGILITY instead */
export const TURN_RATE_AGILITY = TURN_RATE.AGILITY;

/**
 * Free Sprint
 * Sprint is completely free for all players - no segment cost ever
 * This creates a more fast-paced, action-oriented gameplay
 */
export const FREE_SPRINT = {
	/** Score threshold below which sprint is free - set to Infinity for always free sprint */
	SCORE_THRESHOLD: Infinity,
} as const;

/**
 * Sprint System Configuration
 * Sustained sprinting increases score multiplier (NOT speed)
 * Sprint costs 1 segment per 0.5 seconds (unless free sprint active)
 */
export const SPRINT = {
	/** How often a segment is dropped while sprinting (in seconds) */
	SEGMENT_DROP_INTERVAL: 0.5,
	/** Minimum segments required to sprint (can't sprint below this) */
	MIN_SEGMENTS_TO_SPRINT: 3,
	/** Grace period before decay starts when stop sprinting (seconds) */
	DECAY_GRACE_PERIOD: 0.5,
	/** Decay rate multiplier (2x = decays twice as fast as it builds) */
	DECAY_RATE_MULTIPLIER: 2,
	/** Sprint score multiplier tiers - sustained sprinting increases score earned */
	MULTIPLIER_TIERS: [
		{ minSeconds: 0, multiplier: 1.0 },
		{ minSeconds: 5, multiplier: 1.2 },
		{ minSeconds: 10, multiplier: 1.4 },
		{ minSeconds: 20, multiplier: 1.6 },
		{ minSeconds: 30, multiplier: 1.8 },
		{ minSeconds: 60, multiplier: 2.0 },
		{ minSeconds: 600, multiplier: 3.0 }, // 10+ minutes = legendary
	],
} as const;

/**
 * Get sprint score multiplier for accumulated sprint time
 * @param sprintSeconds - Total seconds of sustained sprinting
 * @returns The score multiplier (1.0 to 3.0)
 */
export function getSprintMultiplier(sprintSeconds: number): number {
	let multiplier = 1.0;
	for (const tier of SPRINT.MULTIPLIER_TIERS) {
		if (sprintSeconds >= tier.minSeconds) {
			multiplier = tier.multiplier;
		} else {
			break;
		}
	}
	return multiplier;
}

/** Free sprint score threshold - worms under this score sprint for free */
export const FREE_SPRINT_SCORE_THRESHOLD = FREE_SPRINT.SCORE_THRESHOLD;

/**
 * Segment follow behavior (server-side)
 * Controls how smoothly body segments follow the head
 */
export const SEGMENT_FOLLOW = {
	/** Smoothness factor (0=loose, 0.5=balanced, 1=tight) - higher = tighter following */
	SMOOTHNESS: 0.9,
} as const;

/** @deprecated Use SEGMENT_FOLLOW.SMOOTHNESS instead */
export const SEGMENT_FOLLOW_SMOOTHNESS = SEGMENT_FOLLOW.SMOOTHNESS;

/**
 * Client-side segment physics (spring-damper system)
 * Creates natural ribbon-like following behavior
 */
export const SEGMENT_PHYSICS = {
	/** Spring strength (higher = snappier response) */
	SPRING_STIFFNESS: 15.0,
	/** Velocity damping (higher = less oscillation) */
	DAMPING: 10.0,
	/** Maximum stretch ratio before snap-back */
	MAX_STRETCH: 1.5,
	/**
	 * Segment turn rate (rad/s) - how fast each segment rotates toward segment ahead
	 * Higher = more responsive, Lower = more flowing/organic
	 * Should be higher than worm head turn rate for natural following
	 */
	SEGMENT_TURN_RATE: 8.0,
	/**
	 * Position follow speed - how fast segments move to maintain spacing
	 * Works with turn rate to create organic motion
	 */
	POSITION_FOLLOW_SPEED: 12.0,
} as const;

/** @deprecated Use SEGMENT_PHYSICS.SPRING_STIFFNESS instead */
export const SEGMENT_SPRING_STIFFNESS = SEGMENT_PHYSICS.SPRING_STIFFNESS;
/** @deprecated Use SEGMENT_PHYSICS.DAMPING instead */
export const SEGMENT_DAMPING = SEGMENT_PHYSICS.DAMPING;
/** @deprecated Use SEGMENT_PHYSICS.MAX_STRETCH instead */
export const SEGMENT_MAX_STRETCH = SEGMENT_PHYSICS.MAX_STRETCH;
/** Segment rotation turn rate (rad/s) for direction-based following */
export const SEGMENT_TURN_RATE = SEGMENT_PHYSICS.SEGMENT_TURN_RATE;
/** Segment position follow speed for direction-based following */
export const SEGMENT_POSITION_FOLLOW_SPEED = SEGMENT_PHYSICS.POSITION_FOLLOW_SPEED;

/**
 * Path-following system for smooth body movement
 * Segments trace the exact path the head took
 */
export const PATH_SYSTEM = {
	/** Maximum path points to store (scales for massive worms - 50k * 6 = 300k pixels of history) */
	HISTORY_LENGTH: 50000,
	/** Minimum distance between path points (pixels) - larger value = more history coverage */
	SAMPLE_DISTANCE: 6,
	/** Catmull-Rom tension (0=sharp, 1=smooth) */
	SMOOTHING_TENSION: 0.5,
	/** Convergence speed (higher = snappier following) */
	FOLLOW_SPEED: 20,
} as const;

/** @deprecated Use PATH_SYSTEM.HISTORY_LENGTH instead */
export const PATH_HISTORY_LENGTH = PATH_SYSTEM.HISTORY_LENGTH;
/** @deprecated Use PATH_SYSTEM.SAMPLE_DISTANCE instead */
export const PATH_SAMPLE_DISTANCE = PATH_SYSTEM.SAMPLE_DISTANCE;
/** @deprecated Use PATH_SYSTEM.SMOOTHING_TENSION instead */
export const PATH_SMOOTHING_TENSION = PATH_SYSTEM.SMOOTHING_TENSION;
/** @deprecated Use PATH_SYSTEM.FOLLOW_SPEED instead */
export const PATH_FOLLOW_SPEED = PATH_SYSTEM.FOLLOW_SPEED;

/**
 * Score-based radius scaling
 * Worms grow thicker as they gain more score
 */
export const RADIUS_SCALING = {
	/** Divisor for sqrt(score) scaling (higher = slower growth) */
	SCALE_FACTOR: 25,
	/** Minimum radius (starting size) */
	MIN_RADIUS: WORM.DEFAULT_RADIUS,
	/** Maximum radius cap (prevents giants - smaller for better gameplay at 10k+) */
	MAX_RADIUS: 32,
} as const;

/** @deprecated Use RADIUS_SCALING.SCALE_FACTOR instead */
export const RADIUS_SCALE_FACTOR = RADIUS_SCALING.SCALE_FACTOR;
/** @deprecated Use RADIUS_SCALING.MIN_RADIUS instead */
export const MIN_WORM_RADIUS = RADIUS_SCALING.MIN_RADIUS;
/** @deprecated Use RADIUS_SCALING.MAX_RADIUS instead */
export const MAX_WORM_RADIUS = RADIUS_SCALING.MAX_RADIUS;

/**
 * Calculate the base radius for a worm based on its score
 * Uses square root scaling for balanced growth curve
 *
 * Example values (with SCALE_FACTOR=25, MAX=32):
 * - Score 5:    radius ≈ 16.3
 * - Score 50:   radius ≈ 19.2
 * - Score 100:  radius ≈ 21.0
 * - Score 500:  radius ≈ 28.4
 * - Score 1000+: radius = 32 (capped)
 *
 * @param score - The worm's current score
 * @returns The base radius for segments
 */
export function calculateWormRadius(score: number): number {
	const scaledRadius = RADIUS_SCALING.MIN_RADIUS * (1 + Math.sqrt(score) / RADIUS_SCALING.SCALE_FACTOR);
	return Math.min(scaledRadius, RADIUS_SCALING.MAX_RADIUS);
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                        🍎 FOOD & GROWTH SYSTEM                            ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Food item configuration
 */
export const FOOD = {
	/** Radius of food items */
	RADIUS: 18,
	/** Whether all food items should be the same size (ignoring type multipliers) */
	UNIFORM_SIZE: true,
	/** Collection radius multiplier - makes eating easier without changing visual size */
	COLLECTION_MULTIPLIER: 3.0,
	/** Score value per food item */
	VALUE: 1,
	/** Maximum food items in the world (scaled for larger map) */
	MAX_COUNT: 6000,
	/** Target food density to maintain (scaled for larger map) */
	TARGET_COUNT: 4800,
	/** Food items spawned per second (scaled for larger map) */
	SPAWN_RATE: 80,
	/** Food lifetime before possible decay (5 minutes) */
	LIFETIME_MS: 300000,
	/** Minimum time before decay can start (1 minute) */
	MIN_LIFETIME_MS: 60000,
} as const;

/** @deprecated Use FOOD.RADIUS instead */
export const FOOD_RADIUS = FOOD.RADIUS;
/** @deprecated Use FOOD.VALUE instead */
export const FOOD_VALUE = FOOD.VALUE;
/** @deprecated Use FOOD.MAX_COUNT instead */
export const MAX_FOOD_COUNT = FOOD.MAX_COUNT;
/** @deprecated Use FOOD.SPAWN_RATE instead */
export const FOOD_SPAWN_RATE = FOOD.SPAWN_RATE;
/** @deprecated Use FOOD.LIFETIME_MS instead */
export const FOOD_LIFETIME_MS = FOOD.LIFETIME_MS;
/** @deprecated Use FOOD.MIN_LIFETIME_MS instead */
export const FOOD_MIN_LIFETIME_MS = FOOD.MIN_LIFETIME_MS;
/** @deprecated Use FOOD.TARGET_COUNT instead */
export const TARGET_FOOD_COUNT = FOOD.TARGET_COUNT;

/**
 * Growth and scoring configuration
 *
 * NEW SEGMENT/SCORE SYSTEM:
 * - Players start with 3 segments (head, body, tail) - minimum size
 * - Score 5 (starting) = 3 segments
 * - Score 40 = 4 segments (first growth at 40 points)
 * - Score 100 = 10 segments
 * - After 40, gain 1 segment per 10 points
 */
export const GROWTH = {
	/** Minimum segment count (head, body, tail) */
	MIN_SEGMENTS: 3,
	/** Score threshold before growth starts */
	FIRST_GROWTH_THRESHOLD: 40,
	/** Points needed per segment after threshold */
	POINTS_PER_SEGMENT: 10,
	/** @deprecated Old linear system - use getSegmentsForScore() instead */
	SCORE_PER_SEGMENT: 2,
} as const;

/**
 * Calculate segment count from score
 * - Below 40 score: always 3 segments (minimum)
 * - At 40+: 4 + floor((score - 40) / 10)
 *
 * Examples:
 * - Score 5 (start): 3 segments
 * - Score 40: 4 segments
 * - Score 50: 5 segments
 * - Score 100: 10 segments
 * - Score 200: 20 segments
 */
export function getSegmentsForScore(score: number): number {
	if (score < GROWTH.FIRST_GROWTH_THRESHOLD) {
		return GROWTH.MIN_SEGMENTS;
	}
	return GROWTH.MIN_SEGMENTS + 1 + Math.floor((score - GROWTH.FIRST_GROWTH_THRESHOLD) / GROWTH.POINTS_PER_SEGMENT);
}

/**
 * Calculate score needed for a target segment count
 * Inverse of getSegmentsForScore()
 */
export function getScoreForSegments(segments: number): number {
	if (segments <= GROWTH.MIN_SEGMENTS) {
		return 5; // Starting score
	}
	// segments = MIN + 1 + floor((score - THRESHOLD) / POINTS)
	// segments - MIN - 1 = floor((score - THRESHOLD) / POINTS)
	// (segments - MIN - 1) * POINTS + THRESHOLD = score (minimum)
	return GROWTH.FIRST_GROWTH_THRESHOLD + (segments - GROWTH.MIN_SEGMENTS - 1) * GROWTH.POINTS_PER_SEGMENT;
}

/** @deprecated Use GROWTH.POINTS_PER_SEGMENT or getSegmentsForScore() instead */
export const SCORE_PER_SEGMENT = GROWTH.SCORE_PER_SEGMENT;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                        🍴 FEAST MERGE SYSTEM                              ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Feast tier enum - when food accumulates and merges, it creates exciting high-value targets
 */
export enum FeastTier {
	/** No tier - regular food */
	NONE = 'none',
	/** 15+ value nearby - warm glow, plate icon */
	MEAL = 'meal',
	/** 30+ value - larger plate, steam */
	DINNER = 'dinner',
	/** 50+ value - party cake, sparkles */
	FEAST = 'feast',
	/** 100+ value - champagne burst, confetti */
	BANQUET = 'banquet',
	/** 250+ value - golden table, light rays */
	GOLDEN_BUFFET = 'golden_buffet',
	/** 500+ value - rainbow aura, crown */
	MYTHICAL_FEAST = 'mythical_feast',
	/** 500+ value - Food of the Gods (glowing golden nectar) */
	AMVROSIA = 'amvrosia',
}

/**
 * Feast tier configuration interface
 */
export interface FeastTierConfig {
	tier: FeastTier;
	/** Minimum value threshold to create this feast */
	threshold: number;
	/** Display color for the feast */
	color: number;
	/** Size multiplier relative to base food radius */
	sizeMultiplier: number;
	/** Glow intensity (0-1) */
	glowIntensity: number;
	/** Whether to show particles */
	showParticles: boolean;
	/** Particle count if enabled */
	particleCount: number;
	/** Display name for UI */
	displayName: string;
}

/**
 * Feast merge system configuration
 */
export const FEAST_SYSTEM = {
	/** Radius within which food items can merge */
	MERGE_RADIUS: 60,
	/** Time interval between merge checks (seconds) */
	MERGE_CHECK_INTERVAL: 0.5,
	/** Fresh drop immunity time - food dropped from sprinting cannot merge for this duration (seconds) */
	FRESH_DROP_IMMUNITY_TIME: 5,
	/** Minimum food count needed to trigger merge check */
	MIN_FOOD_FOR_MERGE: 3,
} as const;

/**
 * Feast tier configurations - defines visual and gameplay properties for each tier
 */
export const FEAST_TIER_CONFIGS: Record<FeastTier, FeastTierConfig> = {
	[FeastTier.NONE]: {
		tier: FeastTier.NONE,
		threshold: 0,
		color: 0xffffff,
		sizeMultiplier: 1.0,
		glowIntensity: 0,
		showParticles: false,
		particleCount: 0,
		displayName: '',
	},
	[FeastTier.MEAL]: {
		tier: FeastTier.MEAL,
		threshold: 15,
		color: 0xffa726, // Warm orange
		sizeMultiplier: 1.5,
		glowIntensity: 0.4,
		showParticles: false,
		particleCount: 0,
		displayName: 'MEAL',
	},
	[FeastTier.DINNER]: {
		tier: FeastTier.DINNER,
		threshold: 30,
		color: 0xff7043, // Deep orange
		sizeMultiplier: 1.8,
		glowIntensity: 0.5,
		showParticles: true,
		particleCount: 3,
		displayName: 'DINNER',
	},
	[FeastTier.FEAST]: {
		tier: FeastTier.FEAST,
		threshold: 50,
		color: 0xec407a, // Pink
		sizeMultiplier: 2.2,
		glowIntensity: 0.6,
		showParticles: true,
		particleCount: 6,
		displayName: 'FEAST',
	},
	[FeastTier.BANQUET]: {
		tier: FeastTier.BANQUET,
		threshold: 100,
		color: 0xab47bc, // Purple
		sizeMultiplier: 2.8,
		glowIntensity: 0.75,
		showParticles: true,
		particleCount: 10,
		displayName: 'BANQUET',
	},
	[FeastTier.GOLDEN_BUFFET]: {
		tier: FeastTier.GOLDEN_BUFFET,
		threshold: 250,
		color: 0xffd700, // Gold
		sizeMultiplier: 3.5,
		glowIntensity: 0.9,
		showParticles: true,
		particleCount: 16,
		displayName: 'GOLDEN BUFFET',
	},
	[FeastTier.MYTHICAL_FEAST]: {
		tier: FeastTier.MYTHICAL_FEAST,
		threshold: 400, // Reduced slightly to make room for Amvrosia
		color: 0x00e5ff, // Cyan rainbow
		sizeMultiplier: 4.0,
		glowIntensity: 1.0,
		showParticles: true,
		particleCount: 24,
		displayName: 'MYTHICAL FLOWER',
	},
	[FeastTier.AMVROSIA]: {
		tier: FeastTier.AMVROSIA,
		threshold: 500,
		color: 0xffd700, // Divine Gold
		sizeMultiplier: 4.5,
		glowIntensity: 1.2, // Very bright
		showParticles: true,
		particleCount: 40,
		displayName: 'AMVROSIA',
	},
};

/**
 * Get the appropriate feast tier for a given food value
 * @param value - Total food value
 * @returns The highest tier that the value qualifies for
 */
export function getFeastTierForValue(value: number): FeastTier {
	if (value >= FEAST_TIER_CONFIGS[FeastTier.AMVROSIA].threshold) {
		return FeastTier.AMVROSIA;
	}
	if (value >= FEAST_TIER_CONFIGS[FeastTier.MYTHICAL_FEAST].threshold) {
		return FeastTier.MYTHICAL_FEAST;
	}
	if (value >= FEAST_TIER_CONFIGS[FeastTier.GOLDEN_BUFFET].threshold) {
		return FeastTier.GOLDEN_BUFFET;
	}
	if (value >= FEAST_TIER_CONFIGS[FeastTier.BANQUET].threshold) {
		return FeastTier.BANQUET;
	}
	if (value >= FEAST_TIER_CONFIGS[FeastTier.FEAST].threshold) {
		return FeastTier.FEAST;
	}
	if (value >= FEAST_TIER_CONFIGS[FeastTier.DINNER].threshold) {
		return FeastTier.DINNER;
	}
	if (value >= FEAST_TIER_CONFIGS[FeastTier.MEAL].threshold) {
		return FeastTier.MEAL;
	}
	return FeastTier.NONE;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                        👁️ FOG OF WAR SYSTEM                               ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Size-based visibility system
 * Bigger worms see further (balances being larger targets)
 */
export const FOG_OF_WAR = {
	/** Base visibility radius in world units (high enough to not limit camera view) */
	BASE_RADIUS: 3000,
	/** Additional visibility per body segment */
	RADIUS_PER_SEGMENT: 5,
	/** Minimum visibility radius (matches camera range) */
	MIN_RADIUS: 2500,
	/** Maximum visibility radius (huge worms) */
	MAX_RADIUS: 5000,
	/** Vision powerup visibility multiplier (1.5x per spec) */
	VISION_POWERUP_MULTIPLIER: 1.5,
	/** Sprint visibility bonus tiers */
	SPRINT_VISIBILITY_BONUS: {
		/** Sprint duration for +10% visibility */
		TIER_1_SECONDS: 20,
		TIER_1_BONUS: 0.10,
		/** Sprint duration for +20% visibility */
		TIER_2_SECONDS: 30,
		TIER_2_BONUS: 0.20,
	},
	/** Small worm danger sense - detect big worms at increased range */
	DANGER_SENSE: {
		/** Small worm threshold (under this many segments) */
		SMALL_WORM_SEGMENTS: 20,
		/** Big worm threshold (over this many segments triggers danger sense) */
		BIG_WORM_SEGMENTS: 50,
		/** Detection range multiplier for big worms (1.5x normal range) */
		DETECTION_MULTIPLIER: 1.5,
	},
} as const;

/** @deprecated Use FOG_OF_WAR.BASE_RADIUS instead */
export const FOW_BASE_VISIBILITY_RADIUS = FOG_OF_WAR.BASE_RADIUS;
/** @deprecated Use FOG_OF_WAR.RADIUS_PER_SEGMENT instead */
export const FOW_VISIBILITY_PER_SEGMENT = FOG_OF_WAR.RADIUS_PER_SEGMENT;
/** @deprecated Use FOG_OF_WAR.MIN_RADIUS instead */
export const FOW_MIN_RADIUS = FOG_OF_WAR.MIN_RADIUS;
/** @deprecated Use FOG_OF_WAR.MAX_RADIUS instead */
export const FOW_MAX_RADIUS = FOG_OF_WAR.MAX_RADIUS;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                          🔥 KILL STREAK SYSTEM                            ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Kill Streak System
 * Rapid kills grant brief score multiplier bonuses
 * Timer resets after each kill (rolling window)
 */
export const KILL_STREAK = {
	/** Streak tiers with their requirements and rewards */
	TIERS: [
		{ kills: 3, name: 'RAMPAGE', timeWindow: 30, scoreMultiplier: 1.1, duration: 5, color: 0xff6b35 },
		{ kills: 5, name: 'UNSTOPPABLE', timeWindow: 60, scoreMultiplier: 1.2, duration: 5, color: 0xff4500 },
		{ kills: 7, name: 'GODLIKE', timeWindow: 90, scoreMultiplier: 1.3, duration: 5, color: 0xffd700 },
		{ kills: 10, name: 'LEGENDARY', timeWindow: 120, scoreMultiplier: 1.4, duration: 8, color: 0xff00ff, globalAnnouncement: true },
	],
	/** Visual effects for different streak levels */
	EFFECTS: {
		/** Kills required for orange flame trail */
		FLAME_TRAIL_KILLS: 3,
		/** Kills required for pulsing glow */
		PULSING_GLOW_KILLS: 5,
		/** Kills required for screen edge glow */
		EDGE_GLOW_KILLS: 7,
		/** Kills required for golden aura + particle storm */
		PARTICLE_STORM_KILLS: 10,
	},
} as const;

/**
 * Get kill streak tier for a given kill count within time window
 */
export function getKillStreakTier(kills: number): typeof KILL_STREAK.TIERS[number] | null {
	for (let i = KILL_STREAK.TIERS.length - 1; i >= 0; i--) {
		const tier = KILL_STREAK.TIERS[i];
		if (tier && kills >= tier.kills) {
			return tier;
		}
	}
	return null;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                          👑 BOUNTY SYSTEM                                 ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Bounty System
 * Top 5 players on leaderboard have bounties on their heads
 * Killing them grants bonus segments from death drops
 */
export const BOUNTY = {
	/** Bounty bonuses by rank */
	RANKS: [
		{ rank: 1, bonus: 0.20, name: 'CROWN', color: 0xffd700 },      // +20% - Golden crown
		{ rank: 2, bonus: 0.15, name: 'SILVER', color: 0xc0c0c0 },     // +15% - Silver crown
		{ rank: 3, bonus: 0.12, name: 'BRONZE', color: 0xcd7f32 },     // +12% - Bronze crown
		{ rank: 4, bonus: 0.10, name: 'STAR', color: 0xffff00 },       // +10% - Small star
		{ rank: 5, bonus: 0.05, name: 'STAR', color: 0xffff00 },       // +5%  - Small star
	],
} as const;

/**
 * Get bounty bonus for a given leaderboard rank
 */
export function getBountyBonus(rank: number): number {
	const bounty = BOUNTY.RANKS.find(b => b.rank === rank);
	return bounty ? bounty.bonus : 0;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                          ⚠️ NEAR-MISS FEEDBACK                            ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Near-Miss Feedback System
 * Visual/audio feedback when player barely avoids death
 */
export const NEAR_MISS = {
	/** Near collision threshold (screen edge flash) */
	NEAR_THRESHOLD: 20,
	/** Very close threshold (stronger flash + "CLOSE!" text) */
	VERY_CLOSE_THRESHOLD: 10,
	/** Screen flash intensity (0-1) */
	FLASH_INTENSITY: {
		NEAR: 0.3,
		VERY_CLOSE: 0.6,
	},
	/** Flash duration in milliseconds */
	FLASH_DURATION_MS: 150,
	/** Cooldown between near-miss triggers (prevents spam) */
	COOLDOWN_MS: 500,
} as const;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                          🐌 CRAWL MODE SYSTEM                             ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Crawl Mode Configuration
 * Hold Shift to move slowly, with passive growth and food magnet bonus
 */
export const CRAWL_MODE = {
	/** Speed multiplier (0.7x = 70% of normal) */
	SPEED_MULTIPLIER: 0.7,
	/** Turn rate multiplier (0.8x = reduced to balance tighter turns) */
	TURN_RATE_MULTIPLIER: 0.8,
	/** Passive score gain per second while crawling */
	SCORE_PER_SECOND: 1,
	/** Food magnet bonus - extra pull radius when crawling */
	FOOD_MAGNET_BONUS: 25, // +25 units to pull radius
	/** Key to activate crawl mode */
	ACTIVATION_KEY: 'Shift',
} as const;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                          💀 REVENGE INTEL SYSTEM                          ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Revenge Intel System
 * Shows killer info on death and direction arrow after respawn
 */
export const REVENGE_INTEL = {
	/** Duration to show killer direction arrow after respawn (seconds) */
	ARROW_DURATION: 2.5,
	/** Arrow fade out time (seconds) */
	ARROW_FADE_TIME: 0.5,
	/** Arrow size (pixels) */
	ARROW_SIZE: 40,
	/** Arrow color */
	ARROW_COLOR: 0xff4444,
} as const;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                       🛡️ SPAWN PROTECTION                                 ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Spawn Protection System
 * After spawning or respawning, worms have temporary invulnerability
 * During protection: cannot be killed, cannot eat food, cannot kill others
 * Protection ends early if the worm tries to eat or attack (boosting)
 */
export const SPAWN_PROTECTION = {
	/** Duration of spawn protection in milliseconds (4 seconds) */
	DURATION_MS: 4000,
	/** Visual opacity for protected worms (50% transparent = ghost effect) */
	OPACITY: 0.5,
} as const;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                       🏃 WALL DEATH CREDIT                                ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Wall Death Credit System
 * When a worm dies by hitting the boundary (wall), the closest worm
 * to the dying head gets credited with the kill (rewards the chaser)
 */
export const WALL_DEATH_CREDIT = {
	/** Maximum distance to credit a kill (in world units) */
	MAX_CREDIT_DISTANCE: 500,
	/** Whether to enable wall death credit (can be disabled for testing) */
	ENABLED: true,
} as const;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                       🎯 KILL SCORE SYSTEM                                ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Kill Score System
 * Tracks kills by type and awards earned points at end of game
 * Different kill types have different point values
 */
export const KILL_SCORE = {
	/** Points earned per kill by type */
	POINTS: {
		/** Player kills - highest value */
		PLAYER: 100,
		/** Leviathan (Legendary AI) - varies by mode */
		LEVIATHAN_NORMAL: 1000,  // 10x player in normal mode (rare)
		LEVIATHAN_NIGHTMARE: 100, // Same as player in nightmare mode
		/** Apex AI - dangerous hunters */
		APEX: 25,
		/** Hunter AI - balanced threat */
		HUNTER: 15,
		/** Collector AI - easy targets */
		COLLECTOR: 10,
	},
	/** Display colors for kill types in UI */
	COLORS: {
		PLAYER: 0x00ff88,       // Green - player kills
		LEVIATHAN: 0xff0000,    // Red - legendary
		APEX: 0xff6600,         // Orange - apex
		HUNTER: 0xffcc00,       // Yellow - hunter
		COLLECTOR: 0x88ccff,    // Light blue - collector
	},
	/** Icons/symbols for kill types */
	ICONS: {
		PLAYER: '👤',
		LEVIATHAN: '👑',
		APEX: '🔥',
		HUNTER: '⚔️',
		COLLECTOR: '🌱',
	},
} as const;

/**
 * Kill counts structure for tracking
 */
export interface KillCounts {
	player: number;
	leviathan: number;
	apex: number;
	hunter: number;
	collector: number;
}

/**
 * Calculate total earned points from kills
 * @param kills Kill counts by type
 * @param isNightmareMode Whether nightmare mode is active
 * @returns Total earned points
 */
export function calculateEarnedPoints(kills: KillCounts, isNightmareMode: boolean): number {
	const leviathanPoints = isNightmareMode
		? KILL_SCORE.POINTS.LEVIATHAN_NIGHTMARE
		: KILL_SCORE.POINTS.LEVIATHAN_NORMAL;

	return (
		kills.player * KILL_SCORE.POINTS.PLAYER +
		kills.leviathan * leviathanPoints +
		kills.apex * KILL_SCORE.POINTS.APEX +
		kills.hunter * KILL_SCORE.POINTS.HUNTER +
		kills.collector * KILL_SCORE.POINTS.COLLECTOR
	);
}

/**
 * Get the initial empty kill counts
 */
export function createEmptyKillCounts(): KillCounts {
	return {
		player: 0,
		leviathan: 0,
		apex: 0,
		hunter: 0,
		collector: 0,
	};
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                          ✨ POWERUP SYSTEM                                ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Powerup type definitions
 */
export enum PowerupType {
	// Base Tier - Stack with each other
	MULTIPLIER_2X = 'multiplier_2x',
	MULTIPLIER_4X = 'multiplier_4x',
	// Final Slot - Only one active
	MULTIPLIER_6X = 'multiplier_6x',
	// Override - Temporarily replaces Final Slot
	MULTIPLIER_10X = 'multiplier_10x',
	MULTIPLIER_15X = 'multiplier_15x',
	// Utility powerups
	MAGNET = 'magnet',
	SPEED = 'speed',
	AGILITY = 'agility',
	VISION = 'vision',
}

/**
 * Powerup rarity levels
 */
export enum PowerupRarity {
	COMMON = 'common',
	UNCOMMON = 'uncommon',
	RARE = 'rare',
	EPIC = 'epic',
	LEGENDARY = 'legendary',
	MYTHIC = 'mythic',
}

/**
 * Powerup configuration interface
 */
export interface PowerupConfig {
	type: PowerupType;
	/** Duration in seconds (0 = permanent/stackable) */
	duration: number;
	rarity: PowerupRarity;
	/** Display color */
	color: number;
	/** Pickup radius */
	radius: number;
	/** Spawn weight (higher = more common) */
	spawnWeight: number;
	/** Score points awarded on collection */
	scoreValue: number;
}

/**
 * Powerup spawn rate preset configuration
 */
export interface PowerupRatePreset {
	/** Preset identifier */
	id: string;
	/** Display name */
	name: string;
	/** Description for UI */
	description: string;
	/** Maximum powerups in the world */
	maxCount: number;
	/** Powerups spawned per second */
	spawnRate: number;
	/** Initial powerups spawned at game start */
	initialCount: number;
}

/**
 * Powerup spawn rate presets - from scarce to chaos
 */
export const POWERUP_RATE_PRESETS: Record<string, PowerupRatePreset> = {
	SCARCE: {
		id: 'scarce',
		name: 'Scarce',
		description: 'Very few powerups - strategic gameplay',
		maxCount: 20,
		spawnRate: 0.3,
		initialCount: 5,
	},
	RARE: {
		id: 'rare',
		name: 'Rare',
		description: 'Limited powerups - careful planning',
		maxCount: 35,
		spawnRate: 0.6,
		initialCount: 10,
	},
	NORMAL: {
		id: 'normal',
		name: 'Normal',
		description: 'Balanced powerup distribution',
		maxCount: 50,
		spawnRate: 1.0,
		initialCount: 15,
	},
	ABUNDANT: {
		id: 'abundant',
		name: 'Abundant',
		description: 'Plenty of powerups to collect',
		maxCount: 75,
		spawnRate: 2.0,
		initialCount: 25,
	},
	CHAOS: {
		id: 'chaos',
		name: 'Chaos',
		description: 'Powerups everywhere!',
		maxCount: 120,
		spawnRate: 4.0,
		initialCount: 40,
	},
	MAYHEM: {
		id: 'mayhem',
		name: 'Mayhem',
		description: 'Maximum powerup madness',
		maxCount: 200,
		spawnRate: 8.0,
		initialCount: 75,
	},
	OVERLOAD: {
		id: 'overload',
		name: 'Overload',
		description: 'Absolute powerup insanity!',
		maxCount: 300,
		spawnRate: 15.0,
		initialCount: 100,
	},
};

/** Default powerup rate preset */
export const DEFAULT_POWERUP_RATE_PRESET: PowerupRatePreset = POWERUP_RATE_PRESETS.ABUNDANT!;

/**
 * Get powerup rate preset by ID
 */
export function getPowerupRatePreset(id: string): PowerupRatePreset {
	for (const key of Object.keys(POWERUP_RATE_PRESETS)) {
		const preset = POWERUP_RATE_PRESETS[key];
		if (preset && preset.id === id) {
			return preset;
		}
	}
	return DEFAULT_POWERUP_RATE_PRESET;
}

/**
 * Global powerup system settings
 */
export const POWERUP_SYSTEM = {
	/** Maximum powerups in the world (uses default preset) */
	MAX_COUNT: DEFAULT_POWERUP_RATE_PRESET.maxCount,
	/** Powerups spawned per second (uses default preset) */
	SPAWN_RATE: DEFAULT_POWERUP_RATE_PRESET.spawnRate,
	/** Initial powerups at game start (uses default preset) */
	INITIAL_COUNT: DEFAULT_POWERUP_RATE_PRESET.initialCount,
	/** Powerup lifetime before despawn (5 minutes) */
	LIFETIME_MS: 300000,
	/** Default duration for all powerups in seconds (30 seconds per pickup) */
	DEFAULT_DURATION: 30,
	/** Maximum stacked duration (10 minutes = 600 seconds) */
	MAX_STACK_DURATION: 600,
	/** Maximum speed powerup stacks */
	MAX_SPEED_STACKS: 4,
	/** Speed bonus per stack (15% per stack, 4 stacks = 60% = 1.6x) */
	SPEED_BONUS_PER_STACK: 0.15,
	/** Base magnet pull effect radius (before head scaling) */
	MAGNET_RADIUS: 350,
	/** Magnet head scale - magnet range increases by this × head radius */
	MAGNET_HEAD_SCALE: 4.0,
	/** Magnet pull strength (units/sec) - faster collection */
	MAGNET_PULL_STRENGTH: 400,
} as const;

/** @deprecated Use POWERUP_SYSTEM.MAX_COUNT instead */
export const MAX_POWERUP_COUNT = POWERUP_SYSTEM.MAX_COUNT;
/** @deprecated Use POWERUP_SYSTEM.SPAWN_RATE instead */
export const POWERUP_SPAWN_RATE = POWERUP_SYSTEM.SPAWN_RATE;
/** @deprecated Use POWERUP_SYSTEM.LIFETIME_MS instead */
export const POWERUP_LIFETIME_MS = POWERUP_SYSTEM.LIFETIME_MS;
/** @deprecated Use POWERUP_SYSTEM.MAX_SPEED_STACKS instead */
export const MAX_SPEED_STACKS = POWERUP_SYSTEM.MAX_SPEED_STACKS;
/** @deprecated Use POWERUP_SYSTEM.SPEED_BONUS_PER_STACK instead */
export const SPEED_BONUS_PER_STACK = POWERUP_SYSTEM.SPEED_BONUS_PER_STACK;
/** @deprecated Use POWERUP_SYSTEM.MAGNET_RADIUS instead */
export const MAGNET_RADIUS = POWERUP_SYSTEM.MAGNET_RADIUS;
/** @deprecated Use POWERUP_SYSTEM.MAGNET_HEAD_SCALE instead */
export const MAGNET_HEAD_SCALE = POWERUP_SYSTEM.MAGNET_HEAD_SCALE;
/** @deprecated Use POWERUP_SYSTEM.MAGNET_PULL_STRENGTH instead */
export const MAGNET_PULL_STRENGTH = POWERUP_SYSTEM.MAGNET_PULL_STRENGTH;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                          👁️ VISIBILITY PRESETS                            ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Visibility preset configuration - controls fog of war intensity
 */
export interface VisibilityPreset {
	/** Preset identifier */
	id: string;
	/** Display name */
	name: string;
	/** Description for UI */
	description: string;
	/** Multiplier applied to base visibility radius (1.0 = normal) */
	radiusMultiplier: number;
	/** Whether fog of war is disabled (full map visibility) */
	fogDisabled: boolean;
}

/**
 * Visibility presets - from full vision to extreme fog
 */
export const VISIBILITY_PRESETS: Record<string, VisibilityPreset> = {
	FULL_VISION: {
		id: 'full_vision',
		name: 'Full Vision',
		description: 'No fog of war - see the entire map',
		radiusMultiplier: 10.0, // Effectively infinite
		fogDisabled: true,
	},
	EAGLE_EYE: {
		id: 'eagle_eye',
		name: 'Eagle Eye',
		description: 'Enhanced vision - 2x visibility range',
		radiusMultiplier: 2.0,
		fogDisabled: false,
	},
	NORMAL: {
		id: 'normal',
		name: 'Normal',
		description: 'Standard visibility based on worm size',
		radiusMultiplier: 1.0,
		fogDisabled: false,
	},
	LIMITED: {
		id: 'limited',
		name: 'Limited',
		description: 'Reduced visibility - 75% of normal range',
		radiusMultiplier: 0.75,
		fogDisabled: false,
	},
	DENSE_FOG: {
		id: 'dense_fog',
		name: 'Dense Fog',
		description: 'Heavy fog - 50% visibility range',
		radiusMultiplier: 0.5,
		fogDisabled: false,
	},
	EXTREME_FOG: {
		id: 'extreme_fog',
		name: 'Extreme Fog',
		description: 'Barely visible - 25% visibility range',
		radiusMultiplier: 0.25,
		fogDisabled: false,
	},
};

/** Default visibility preset */
export const DEFAULT_VISIBILITY_PRESET: VisibilityPreset = VISIBILITY_PRESETS.NORMAL!;

/**
 * Get visibility preset by ID
 */
export function getVisibilityPreset(id: string): VisibilityPreset {
	for (const key of Object.keys(VISIBILITY_PRESETS)) {
		const preset = VISIBILITY_PRESETS[key];
		if (preset && preset.id === id) {
			return preset;
		}
	}
	return DEFAULT_VISIBILITY_PRESET;
}

/**
 * Segment bonus when collecting any powerup (+5 segments per spec)
 */
export const POWERUP_SEGMENT_BONUS = 5;

/**
 * Individual powerup configurations
 * Defines behavior for each powerup type
 * All powerups grant +5 segments on collection plus their effect!
 */
export const POWERUP_CONFIGS: Record<PowerupType, PowerupConfig> = {
	// ─────────────────────────────────────────────────────────────────────────
	// MULTIPLIERS - Increase score gain temporarily (30s per pickup, stackable to 10 min)
	// Per spec: 2x (common), 4x (uncommon), 6x (rare)
	// ─────────────────────────────────────────────────────────────────────────
	[PowerupType.MULTIPLIER_2X]: {
		type: PowerupType.MULTIPLIER_2X,
		duration: 30,  // 30 seconds per pickup, stacks up to 10 min
		rarity: PowerupRarity.COMMON,
		color: 0x00bcd4,  // Cyan
		radius: 12,
		spawnWeight: 30,
		scoreValue: 50, // +5 segments worth (GROWTH.POINTS_PER_SEGMENT * 5)
	},
	[PowerupType.MULTIPLIER_4X]: {
		type: PowerupType.MULTIPLIER_4X,
		duration: 30,  // 30 seconds per pickup, stacks up to 10 min
		rarity: PowerupRarity.UNCOMMON,
		color: 0x00bcd4,  // Cyan
		radius: 14,
		spawnWeight: 15,
		scoreValue: 50, // +5 segments worth
	},
	[PowerupType.MULTIPLIER_6X]: {
		type: PowerupType.MULTIPLIER_6X,
		duration: 30,  // 30 seconds per pickup, stacks up to 10 min
		rarity: PowerupRarity.RARE,
		color: 0x00bcd4,  // Bright Cyan
		radius: 16,
		spawnWeight: 5,  // Rare but not ultra-rare
		scoreValue: 50, // +5 segments worth
	},
	// Override Multipliers - Temporarily replace Final Slot (6x)
	// These do NOT stack time - each pickup is a fresh 30s
	[PowerupType.MULTIPLIER_10X]: {
		type: PowerupType.MULTIPLIER_10X,
		duration: 30,  // Fresh 30 seconds (does not stack!)
		rarity: PowerupRarity.EPIC,
		color: 0xffd700,  // Bright Gold - Epic
		radius: 18,
		spawnWeight: 1.5,  // Epic rarity - rare but findable
		scoreValue: 100, // +10 segments worth
	},
	[PowerupType.MULTIPLIER_15X]: {
		type: PowerupType.MULTIPLIER_15X,
		duration: 30,  // Fresh 30 seconds (does not stack!)
		rarity: PowerupRarity.LEGENDARY,
		color: 0xff00ff,  // Magenta/Pink - Legendary (very rare!)
		radius: 20,
		spawnWeight: 0.5,  // Legendary rarity - very rare
		scoreValue: 150, // +15 segments worth
	},
	// ─────────────────────────────────────────────────────────────────────────
	// UTILITY POWERUPS (30s per pickup, stackable to 10 min)
	// ─────────────────────────────────────────────────────────────────────────
	[PowerupType.MAGNET]: {
		type: PowerupType.MAGNET,
		duration: 30,  // 30 seconds per pickup, stacks up to 10 min
		rarity: PowerupRarity.COMMON,
		color: 0x9c27b0,  // Purple
		radius: 12,
		spawnWeight: 40,  // Increased for more frequent spawns
		scoreValue: 50, // +5 segments worth
	},
	[PowerupType.SPEED]: {
		type: PowerupType.SPEED,
		duration: 30,  // 30 seconds per pickup, stacks up to 10 min
		rarity: PowerupRarity.COMMON,
		color: 0xff9800,  // Orange
		radius: 10,
		spawnWeight: 35,  // Increased for more frequent spawns
		scoreValue: 50, // +5 segments worth
	},
	[PowerupType.AGILITY]: {
		type: PowerupType.AGILITY,
		duration: 30,  // 30 seconds per pickup, stacks up to 10 min
		rarity: PowerupRarity.COMMON,  // Changed from UNCOMMON to COMMON
		color: 0x4caf50,  // Green
		radius: 12,
		spawnWeight: 30,  // Increased for more frequent spawns
		scoreValue: 50, // +5 segments worth
	},
	[PowerupType.VISION]: {
		type: PowerupType.VISION,
		duration: 30,  // 30 seconds per pickup, stacks up to 10 min
		rarity: PowerupRarity.UNCOMMON,
		color: 0x03a9f4,  // Light Blue (eye symbol 👁)
		radius: 12,
		spawnWeight: 15,
		scoreValue: 50, // +5 segments worth
	},
};

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                         🎨 VISUAL CUSTOMIZATION                           ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Available worm colors
 * Bright, saturated colors for the retro arcade aesthetic
 */
export const WORM_COLORS = [
	0xff6b6b,  // 🔴 Coral Red
	0x4ecdc4,  // 🔵 Teal
	0xffe66d,  // 🟡 Golden Yellow
	0x95e1d3,  // 🟢 Mint Green
	0xf38181,  // 🟠 Salmon
	0xaa96da,  // 🟣 Lavender
	0xfcbad3,  // 🩷 Cotton Candy Pink
	0xa8d8ea,  // 🩵 Sky Blue
	0xf9ed69,  // 🍋 Lemon
	0xb8de6f,  // 🍀 Lime Green
] as const;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                       🗺️ ZONE-BASED FOOD DISTRIBUTION                     ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * World zones for food distribution
 * CORE = safe farming area (center)
 * MID = balanced play (middle ring)
 * RIM = high risk/reward (outer edge)
 */
export enum WorldZone {
	CORE = 'core',
	MID = 'mid',
	RIM = 'rim',
}

/**
 * Food category for spawn rates
 * JUNK = low value (0.5-3 segments)
 * REGULAR = medium value (4-6 segments)
 * HEALTHY = high value (7-10 segments)
 */
export enum FoodCategory {
	JUNK = 'junk',
	REGULAR = 'regular',
	HEALTHY = 'healthy',
}

/**
 * Food type enum - Individual food items
 * Health-themed: healthier foods give more segments!
 */
export enum FoodType {
	// ═══════════════════════════════════════════════════════════════════════════
	// JUNK FOOD (0.5-2 segments) - 73% spawn rate - Low value, waste of time!
	// ═══════════════════════════════════════════════════════════════════════════
	CANDY = 'candy',           // +0.5 segments
	LOLLIPOP = 'lollipop',     // +0.5 segments
	COOKIE = 'cookie',         // +0.5 segments
	SODA = 'soda',             // +0.5 segments
	DONUT = 'donut',           // +1 segment
	CHOCOLATE = 'chocolate',   // +1 segment
	FRIES = 'fries',           // +1.5 segments
	PIZZA = 'pizza',           // +2 segments
	HOT_DOG = 'hot_dog',       // +2 segments
	TACO = 'taco',             // +2.5 segments
	BURGER = 'burger',         // +3 segments
	POPCORN = 'popcorn',       // +2 segments

	// ═══════════════════════════════════════════════════════════════════════════
	// REGULAR FOOD (4-6 segments) - 19% spawn rate
	// ═══════════════════════════════════════════════════════════════════════════
	EGG = 'egg',               // +4 segments
	BREAD = 'bread',           // +4 segments
	CHEESE = 'cheese',         // +5 segments
	CHICKEN = 'chicken',       // +5 segments
	RICE = 'rice',             // +6 segments
	RAMEN = 'ramen',           // +6 segments
	STEAK = 'steak',           // +5 segments

	// ═══════════════════════════════════════════════════════════════════════════
	// HEALTHY FOOD (7-10 segments) - 8% spawn rate
	// ═══════════════════════════════════════════════════════════════════════════
	APPLE = 'apple',           // +7 segments
	CARROT = 'carrot',         // +7 segments
	GRAPES = 'grapes',         // +8 segments
	BROCCOLI = 'broccoli',     // +8 segments
	STRAWBERRY = 'strawberry', // +9 segments
	SUSHI_ROLL = 'sushi_roll', // +10 segments
	AVOCADO = 'avocado',       // +10 segments

	// ═══════════════════════════════════════════════════════════════════════════
	// ESSENCE (Death drops)
	// ═══════════════════════════════════════════════════════════════════════════
	ESSENCE = 'essence',       // +1 segment (from worm death)
}

/**
 * Food type configuration interface
 */
export interface FoodTypeConfig {
	type: FoodType;
	category: FoodCategory;
	/** Segment value (can be fractional, accumulated) */
	value: number;
	/** Spawn weight within category (higher = more common) */
	spawnWeight: number;
	/** Display color */
	color: number;
	/** Visual size multiplier relative to base radius */
	sizeMultiplier: number;
}

/**
 * Complete food type configurations
 * Values from GAME_REQUIREMENTS.md Food Ecosystem section
 */
export const FOOD_TYPE_CONFIGS: Record<FoodType, FoodTypeConfig> = {
	// ═══════════════════════════════════════════════════════════════════════════
	// JUNK FOOD - Small, colorful, everywhere but low value
	// ═══════════════════════════════════════════════════════════════════════════
	[FoodType.CANDY]: {
		type: FoodType.CANDY,
		category: FoodCategory.JUNK,
		value: 0.5,
		spawnWeight: 12,
		color: 0xff69b4, // Hot pink
		sizeMultiplier: 0.6,
	},
	[FoodType.LOLLIPOP]: {
		type: FoodType.LOLLIPOP,
		category: FoodCategory.JUNK,
		value: 0.5,
		spawnWeight: 10,
		color: 0xff4500, // Red-orange swirl
		sizeMultiplier: 0.65,
	},
	[FoodType.COOKIE]: {
		type: FoodType.COOKIE,
		category: FoodCategory.JUNK,
		value: 0.5,
		spawnWeight: 12,
		color: 0xd2691e, // Chocolate brown
		sizeMultiplier: 0.7,
	},
	[FoodType.SODA]: {
		type: FoodType.SODA,
		category: FoodCategory.JUNK,
		value: 0.5,
		spawnWeight: 10,
		color: 0xdc143c, // Crimson red (cola)
		sizeMultiplier: 0.75,
	},
	[FoodType.DONUT]: {
		type: FoodType.DONUT,
		category: FoodCategory.JUNK,
		value: 1,
		spawnWeight: 8,
		color: 0xffb6c1, // Light pink frosting
		sizeMultiplier: 0.8,
	},
	[FoodType.CHOCOLATE]: {
		type: FoodType.CHOCOLATE,
		category: FoodCategory.JUNK,
		value: 1,
		spawnWeight: 8,
		color: 0x8b4513, // Saddle brown
		sizeMultiplier: 0.85,
	},
	[FoodType.FRIES]: {
		type: FoodType.FRIES,
		category: FoodCategory.JUNK,
		value: 1.5,
		spawnWeight: 6,
		color: 0xffd700, // Golden yellow
		sizeMultiplier: 0.85,
	},
	[FoodType.PIZZA]: {
		type: FoodType.PIZZA,
		category: FoodCategory.JUNK,
		value: 2,
		spawnWeight: 4,
		color: 0xff6347, // Tomato red
		sizeMultiplier: 0.95,
	},
	[FoodType.HOT_DOG]: {
		type: FoodType.HOT_DOG,
		category: FoodCategory.JUNK,
		value: 2,
		spawnWeight: 3,
		color: 0xcd853f, // Peru (bun color)
		sizeMultiplier: 0.95,
	},

	[FoodType.TACO]: {
		type: FoodType.TACO,
		category: FoodCategory.JUNK,
		value: 2.5,
		spawnWeight: 3,
		color: 0xffd700,
		sizeMultiplier: 0.95,
	},
	[FoodType.BURGER]: {
		type: FoodType.BURGER,
		category: FoodCategory.JUNK,
		value: 3,
		spawnWeight: 2,
		color: 0xcd853f,
		sizeMultiplier: 0.95,
	},
	[FoodType.POPCORN]: {
		type: FoodType.POPCORN,
		category: FoodCategory.JUNK,
		value: 2,
		spawnWeight: 3,
		color: 0xffffff,
		sizeMultiplier: 0.9,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// REGULAR FOOD - Medium size, balanced value
	// ═══════════════════════════════════════════════════════════════════════════
	[FoodType.EGG]: {
		type: FoodType.EGG,
		category: FoodCategory.REGULAR,
		value: 4,
		spawnWeight: 6,
		color: 0xfffaf0, // Floral white
		sizeMultiplier: 1.0,
	},
	[FoodType.BREAD]: {
		type: FoodType.BREAD,
		category: FoodCategory.REGULAR,
		value: 4,
		spawnWeight: 5,
		color: 0xdeb887, // Burlywood (bread crust)
		sizeMultiplier: 1.0,
	},
	[FoodType.CHEESE]: {
		type: FoodType.CHEESE,
		category: FoodCategory.REGULAR,
		value: 5,
		spawnWeight: 4,
		color: 0xffc125, // Goldenrod yellow
		sizeMultiplier: 1.05,
	},
	[FoodType.CHICKEN]: {
		type: FoodType.CHICKEN,
		category: FoodCategory.REGULAR,
		value: 5,
		spawnWeight: 3,
		color: 0xf4a460, // Sandy brown (cooked chicken)
		sizeMultiplier: 1.1,
	},
	[FoodType.RICE]: {
		type: FoodType.RICE,
		category: FoodCategory.REGULAR,
		value: 6,
		spawnWeight: 1,
		color: 0xf5f5f5, // White smoke
		sizeMultiplier: 1.1,
	},
	[FoodType.RAMEN]: {
		type: FoodType.RAMEN,
		category: FoodCategory.REGULAR,
		value: 6,
		spawnWeight: 2,
		color: 0xf5deb3,
		sizeMultiplier: 1.1,
	},
	[FoodType.STEAK]: {
		type: FoodType.STEAK,
		category: FoodCategory.REGULAR,
		value: 5,
		spawnWeight: 2,
		color: 0x8b0000,
		sizeMultiplier: 1.1,
	},

	// ═══════════════════════════════════════════════════════════════════════════
	// HEALTHY FOOD - Larger, glowing, rare but powerful!
	// ═══════════════════════════════════════════════════════════════════════════
	[FoodType.APPLE]: {
		type: FoodType.APPLE,
		category: FoodCategory.HEALTHY,
		value: 7,
		spawnWeight: 2.5,
		color: 0xff0000, // Bright red
		sizeMultiplier: 1.2,
	},
	[FoodType.CARROT]: {
		type: FoodType.CARROT,
		category: FoodCategory.HEALTHY,
		value: 7,
		spawnWeight: 2.5,
		color: 0xff8c00, // Dark orange
		sizeMultiplier: 1.2,
	},
	[FoodType.GRAPES]: {
		type: FoodType.GRAPES,
		category: FoodCategory.HEALTHY,
		value: 8,
		spawnWeight: 1.5,
		color: 0x9400d3, // Dark violet
		sizeMultiplier: 1.25,
	},
	[FoodType.BROCCOLI]: {
		type: FoodType.BROCCOLI,
		category: FoodCategory.HEALTHY,
		value: 8,
		spawnWeight: 1,
		color: 0x228b22, // Forest green
		sizeMultiplier: 1.3,
	},
	[FoodType.STRAWBERRY]: {
		type: FoodType.STRAWBERRY,
		category: FoodCategory.HEALTHY,
		value: 9,
		spawnWeight: 0.5,
		color: 0xff1493, // Deep pink
		sizeMultiplier: 1.35,
	},
	[FoodType.SUSHI_ROLL]: {
		type: FoodType.SUSHI_ROLL,
		category: FoodCategory.HEALTHY,
		value: 10,
		spawnWeight: 1,
		color: 0x006400,
		sizeMultiplier: 1.4,
	},
	[FoodType.AVOCADO]: {
		type: FoodType.AVOCADO,
		category: FoodCategory.HEALTHY,
		value: 10,
		spawnWeight: 0.5,
		color: 0x9acd32, // Yellow green
		sizeMultiplier: 1.4,
	},

	// ═══════════════════════════════════════════════════════════════════════════
	// ESSENCE - Death drops (neutral energy)
	// ═══════════════════════════════════════════════════════════════════════════
	[FoodType.ESSENCE]: {
		type: FoodType.ESSENCE,
		category: FoodCategory.JUNK, // Categorized with junk but not world-spawned
		value: 1,
		spawnWeight: 0, // Not world-spawned
		color: 0x87ceeb, // Light sky blue
		sizeMultiplier: 0.8,
	},
};

/**
 * Food category spawn weights (global)
 * Total: 73% junk + 19% regular + 8% healthy = 100%
 */
export const FOOD_CATEGORY_WEIGHTS = {
	[FoodCategory.JUNK]: 73,
	[FoodCategory.REGULAR]: 19,
	[FoodCategory.HEALTHY]: 8,
} as const;

/**
 * Get all food types for a category
 */
export function getFoodTypesForCategory(category: FoodCategory): FoodType[] {
	return Object.values(FOOD_TYPE_CONFIGS)
		.filter(config => config.category === category && config.spawnWeight > 0)
		.map(config => config.type);
}

/**
 * Select a random food type from a specific category
 */
export function selectFoodTypeFromCategory(category: FoodCategory): FoodType {
	const categoryFoods = Object.values(FOOD_TYPE_CONFIGS).filter(
		config => config.category === category && config.spawnWeight > 0
	);

	if (categoryFoods.length === 0) {
		return FoodType.CANDY;
	}

	const totalWeight = categoryFoods.reduce((sum, config) => sum + config.spawnWeight, 0);
	let roll = Math.random() * totalWeight;

	for (const config of categoryFoods) {
		roll -= config.spawnWeight;
		if (roll <= 0) {
			return config.type;
		}
	}

	return categoryFoods[0]?.type ?? FoodType.CANDY;
}

/**
 * Get food config by type
 */
export function getFoodConfig(type: FoodType): FoodTypeConfig {
	return FOOD_TYPE_CONFIGS[type];
}

/**
 * Zone boundary thresholds (percentage of world radius)
 */
export const ZONE_BOUNDARIES = {
	/** CORE zone: 0% to 33% of world radius */
	CORE_END: 0.33,
	/** MID zone: 33% to 66% of world radius */
	MID_END: 0.66,
	/** RIM zone: 66% to 100% of world radius */
	RIM_END: 1.0,
} as const;

/**
 * Zone-based spawn rates for each food category
 *
 * DESIGN PHILOSOPHY (Flattened Distribution):
 * - No zone is "bad" - players can grow anywhere at slightly different rates
 * - Edge still rewards risk (25% vs 15% healthy) but isn't mandatory
 * - Death drops and feasts matter more than foraging patterns
 * - Players spread across map, creating unpredictable gameplay
 * - Predators can't camp one zone waiting for all the prey
 */
export const ZONE_SPAWN_RATES = {
	/** CORE zone (0-33%): Safe, steady growth - viable for all playstyles */
	[WorldZone.CORE]: {
		[FoodCategory.JUNK]: 0.55,     // 55% junk food
		[FoodCategory.REGULAR]: 0.30,  // 30% regular food
		[FoodCategory.HEALTHY]: 0.15,  // 15% healthy food
	},
	/** MID zone (33-66%): Balanced play - good mix of all food types */
	[WorldZone.MID]: {
		[FoodCategory.JUNK]: 0.45,     // 45% junk food
		[FoodCategory.REGULAR]: 0.35,  // 35% regular food
		[FoodCategory.HEALTHY]: 0.20,  // 20% healthy food
	},
	/** RIM zone (66-100%): Slight edge bonus - rewards risk-takers */
	[WorldZone.RIM]: {
		[FoodCategory.JUNK]: 0.40,     // 40% junk food
		[FoodCategory.REGULAR]: 0.35,  // 35% regular food
		[FoodCategory.HEALTHY]: 0.25,  // 25% healthy food
	},
} as const;

/**
 * Food values by category
 * Used to determine segment gain when eating
 */
export const FOOD_VALUES = {
	/** Junk food: 0.5-2 segment value (punishing!) */
	[FoodCategory.JUNK]: { min: 0.5, max: 2 },
	/** Regular food: 4-6 segment value */
	[FoodCategory.REGULAR]: { min: 4, max: 6 },
	/** Healthy food: 7-10 segment value */
	[FoodCategory.HEALTHY]: { min: 7, max: 10 },
} as const;

/**
 * Food colors by category for visual distinction
 */
export const FOOD_COLORS_BY_CATEGORY = {
	/** Junk food: muted/candy colors */
	[FoodCategory.JUNK]: [
		0xffa07a, // Light salmon (candy)
		0xf0e68c, // Khaki (cookie)
		0xdda0dd, // Plum (lollipop)
		0xffc0cb, // Pink (bubblegum)
		0xffb347, // Pastel orange (chips)
	],
	/** Regular food: neutral/warm colors */
	[FoodCategory.REGULAR]: [
		0xf5deb3, // Wheat (bread)
		0xffd700, // Gold (cheese)
		0xffa500, // Orange (chicken)
		0xfffacd, // Lemon chiffon (egg)
		0xf5f5dc, // Beige (rice)
	],
	/** Healthy food: vibrant green/red colors */
	[FoodCategory.HEALTHY]: [
		0xff6347, // Tomato (apple)
		0xff4500, // Orange red (strawberry)
		0x32cd32, // Lime green (broccoli)
		0x9acd32, // Yellow green (avocado)
		0x8fbc8f, // Dark sea green (grapes)
	],
} as const;

/**
 * Determine which zone a position is in based on distance from center
 * @param x - X coordinate
 * @param y - Y coordinate
 * @param worldRadius - The world radius
 * @returns The zone (CORE, MID, or RIM)
 */
export function getWorldZone(x: number, y: number, worldRadius: number): WorldZone {
	const distanceFromCenter = Math.sqrt(x * x + y * y);
	const normalizedDistance = distanceFromCenter / worldRadius;

	if (normalizedDistance <= ZONE_BOUNDARIES.CORE_END) {
		return WorldZone.CORE;
	} else if (normalizedDistance <= ZONE_BOUNDARIES.MID_END) {
		return WorldZone.MID;
	} else {
		return WorldZone.RIM;
	}
}

/**
 * Get a random food category based on zone spawn rates
 * @param zone - The world zone
 * @returns A random food category weighted by zone spawn rates
 */
export function getRandomFoodCategory(zone: WorldZone): FoodCategory {
	const rates = ZONE_SPAWN_RATES[zone];
	const random = Math.random();

	if (random < rates[FoodCategory.JUNK]) {
		return FoodCategory.JUNK;
	} else if (random < rates[FoodCategory.JUNK] + rates[FoodCategory.REGULAR]) {
		return FoodCategory.REGULAR;
	} else {
		return FoodCategory.HEALTHY;
	}
}

/**
 * Get a random food value based on category
 * @param category - The food category
 * @returns A random value within the category's range
 */
export function getRandomFoodValue(category: FoodCategory): number {
	const range = FOOD_VALUES[category];
	return range.min + Math.random() * (range.max - range.min);
}

/**
 * Get a random color for a food category
 * @param category - The food category
 * @returns A random color from the category's color palette
 */
export function getRandomFoodColor(category: FoodCategory): number {
	const colors = FOOD_COLORS_BY_CATEGORY[category];
	return colors[Math.floor(Math.random() * colors.length)] ?? 0x4ecdc4;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                          📡 GAME STATES & MESSAGES                        ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Game state enum for room management
 */
export enum GameState {
	WAITING = 'waiting',
	PLAYING = 'playing',
	GAME_OVER = 'game_over',
}

/**
 * Message types for client-server communication
 */
export enum MessageType {
	INPUT = 'input',
	BOOST_START = 'boost_start',
	BOOST_END = 'boost_end',
	PLAYER_DIED = 'player_died',
	POWERUP_COLLECTED = 'powerup_collected',
	SUICIDE = 'suicide',
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                          📚 MODULE REFERENCES                             ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

// Math utilities: import { MathUtils } from '@wooorms/shared' (see ./math.ts)
// AI characters: import { AI_CHARACTERS, AI_PERSONALITIES } from '@wooorms/shared' (see ./ai.ts)
// Skin system: import { SKINS, getSkinsByRarity } from '@wooorms/shared' (see ./skins.ts)
